import { prefersReducedMotion } from '@theme/utilities';
import { Component } from '@theme/component';

class TimelineScroll extends Component {
  /** @type {HTMLElement[]} */
  #items = [];
  #ticking = false;

  #onScroll = () => this.#requestUpdate();

  connectedCallback() {
    super.connectedCallback();

    this.#items = this.#getItems();
    if (!this.#items.length) return;

    for (const item of this.#items) item.classList.add('timeline-scroll__item');

    // Reduced motion / no JS: leave items fully visible.
    if (prefersReducedMotion()) return;

    this.classList.add('is-ready');

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
  }

  #requestUpdate() {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#ticking = false;
      this.#update();
    });
  }

  #update() {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const start = viewportHeight * this.#fadeStart;
    const end = viewportHeight * this.#fadeEnd;
    const span = start - end || 1;

    const tops = this.#items.map((item) => item.getBoundingClientRect().top);

    const first = tops[0] ?? 0;
    const second = tops[1] ?? 0;
    const horizontal = tops.length > 1 && Math.abs(second - first) < 8;
    const stagger = this.#stagger;

    this.#items.forEach((item, index) => {
      const referenceTop = horizontal ? first : tops[index] ?? 0;
      const offset = horizontal ? index * stagger : 0;
      const opacity = Math.min(Math.max((start - referenceTop) / span - offset, 0), 1);
      item.style.setProperty('--ts-opacity', opacity.toFixed(3));
    });
  }

  get #stagger() {
    const value = Number.parseFloat(this.dataset.stagger ?? '0.4');
    if (Number.isNaN(value)) return 0.4;
    return Math.max(value, 0);
  }

  get #fadeStart() {
    const value = Number.parseFloat(this.dataset.fadeStart ?? '1');
    if (Number.isNaN(value)) return 1;
    return Math.min(Math.max(value, 0), 1);
  }

  get #fadeEnd() {
    const value = Number.parseFloat(this.dataset.fadeEnd ?? '0.55');
    if (Number.isNaN(value)) return 0.55;
    return Math.min(Math.max(value, 0), 1);
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getItems() {
    const selector = this.dataset.itemSelector || '.custom-grid-column';
    const explicit = this.querySelectorAll('[data-timeline-item]');
    const nodes = explicit.length ? explicit : this.querySelectorAll(selector);
    return Array.from(nodes).filter(
      /** @returns {node is HTMLElement} */
      (node) => node instanceof HTMLElement
    );
  }
}

if (!customElements.get('timeline-scroll')) {
  customElements.define('timeline-scroll', TimelineScroll);
}
